import React, { createContext, useCallback, useContext, useState } from "react";
import { Snackbar, Alert, Slide, type SlideProps } from "@mui/material";
import { colors } from "../../theme/colors";

type ToastSeverity = "error" | "success" | "warning" | "info";

interface ToastOptions {
  persistent?: boolean;
  autoHideDuration?: number;
  action?: React.ReactNode;
}

interface ToastItem {
  id: number;
  message: string;
  severity: ToastSeverity;
  persistent: boolean;
  autoHideDuration: number;
  action?: React.ReactNode;
}

interface ToastApi {
  error: (message: string, opts?: ToastOptions) => void;
  success: (message: string, opts?: ToastOptions) => void;
  warning: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATIONS: Record<ToastSeverity, number> = {
  error: 6000,
  success: 3000,
  warning: 6000,
  info: 3000,
};

const DEFAULT_PERSISTENT: Record<ToastSeverity, boolean> = {
  error: true,
  success: false,
  warning: false,
  info: false,
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

let nextId = 0;

const SEVERITY_COLORS: Record<ToastSeverity, { bg: string; fg: string }> = {
  success: { bg: colors.status.success, fg: "#ffffff" },
  error: { bg: colors.status.error, fg: "#ffffff" },
  warning: { bg: "#f59e0b", fg: "#ffffff" },
  info: { bg: colors.status.info, fg: "#ffffff" },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<ToastItem[]>([]);
  const [current, setCurrent] = useState<ToastItem | null>(null);
  const [open, setOpen] = useState(false);

  const processNext = useCallback((items: ToastItem[]) => {
    if (items.length > 0) {
      setCurrent(items[0]);
      setQueue(items.slice(1));
      setOpen(true);
    } else {
      setCurrent(null);
    }
  }, []);

  const enqueue = useCallback(
    (message: string, severity: ToastSeverity, opts?: ToastOptions) => {
      const item: ToastItem = {
        id: nextId++,
        message,
        severity,
        persistent: opts?.persistent ?? DEFAULT_PERSISTENT[severity],
        autoHideDuration: opts?.autoHideDuration ?? DEFAULT_DURATIONS[severity],
        action: opts?.action,
      };

      if (current === null) {
        setCurrent(item);
        setOpen(true);
      } else {
        setQueue((prev) => [...prev, item]);
      }
    },
    [current],
  );

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") return;
      setOpen(false);
    },
    [],
  );

  const handleExited = useCallback(() => {
    processNext(queue);
  }, [queue, processNext]);

  const api = React.useMemo<ToastApi>(
    () => ({
      error: (msg, opts?) => enqueue(msg, "error", opts),
      success: (msg, opts?) => enqueue(msg, "success", opts),
      warning: (msg, opts?) => enqueue(msg, "warning", opts),
      info: (msg, opts?) => enqueue(msg, "info", opts),
    }),
    [enqueue],
  );

  const severityColors = current
    ? SEVERITY_COLORS[current.severity]
    : SEVERITY_COLORS.info;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={current?.persistent ? null : current?.autoHideDuration}
        TransitionComponent={SlideTransition}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            action={current.action}
            sx={{
              maxWidth: { xs: "90vw", sm: 600 },
              minWidth: 300,
              backgroundColor: severityColors.bg,
              color: severityColors.fg,
              fontSize: "0.875rem",
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              "& .MuiAlert-icon": { color: severityColors.fg },
              "& .MuiAlert-message": { padding: "2px 0" },
              "& .MuiAlert-action": {
                paddingTop: 0,
                paddingBottom: 0,
                "& .MuiIconButton-root": {
                  color: severityColors.fg,
                  opacity: 0.8,
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}
